import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import Cookies from 'js-cookie';
import { ThemeType, THEME_COOKIE_NAME } from '../../types/themeTypes';
import webViewStore from './webViewStore';

// 트리쉐이킹을 위해 필요한 것만 정의함
// https://culorijs.org/guides/tree-shaking/
import { useMode, formatHex, modeOklch } from 'culori/fn';

const oklch = useMode(modeOklch);

export const lightOklchColors = {
    base100: 'oklch(100% 0 0)',
    base200: 'oklch(98% 0 0)',
    base300: 'oklch(95% 0 0)',
    baseContent: 'oklch(21% 0.006 285.885)',
    primary: 'oklch(45% 0.24 277.023)',
    primaryContent: 'oklch(93% 0.034 272.788)',
    secondary: 'oklch(65% 0.241 354.308)',
    secondaryContent: 'oklch(94% 0.028 342.258)',
    accent: 'oklch(77% 0.152 181.912)',
    accentContent: 'oklch(38% 0.063 188.416)',
    neutral: 'oklch(14% 0.005 285.823)',
    neutralContent: 'oklch(92% 0.004 286.32)',
    info: 'oklch(74% 0.16 232.661)',
    infoContent: 'oklch(29% 0.066 243.157)',
    success: 'oklch(76% 0.177 163.223)',
    successContent: 'oklch(37% 0.077 168.94)',
    warning: 'oklch(82% 0.189 84.429)',
    warningContent: 'oklch(41% 0.112 45.904)',
    error: 'oklch(71% 0.194 13.428)',
    errorContent: 'oklch(27% 0.105 12.094)',
    up: '#f04251',
    down: '#3485fa',
} as const;

export const darkOklchColors = {
    base100: 'oklch(25.33% 0.016 252.42)',
    base200: 'oklch(23.26% 0.014 253.1)',
    base300: 'oklch(21.15% 0.012 254.09)',
    baseContent: 'oklch(97.807% 0.029 256.847)',
    primary: 'oklch(58% 0.233 277.117)',
    primaryContent: 'oklch(96% 0.018 272.314)',
    secondary: 'oklch(65% 0.241 354.308)',
    secondaryContent: 'oklch(94% 0.028 342.258)',
    accent: 'oklch(77% 0.152 181.912)',
    accentContent: 'oklch(38% 0.063 188.416)',
    neutral: 'oklch(14% 0.005 285.823)',
    neutralContent: 'oklch(92% 0.004 286.32)',
    info: 'oklch(74% 0.16 232.661)',
    infoContent: 'oklch(29% 0.066 243.157)',
    success: 'oklch(76% 0.177 163.223)',
    successContent: 'oklch(37% 0.077 168.94)',
    warning: 'oklch(82% 0.189 84.429)',
    warningContent: 'oklch(41% 0.112 45.904)',
    error: 'oklch(71% 0.194 13.428)',
    errorContent: 'oklch(27% 0.105 12.094)',
    up: '#f04251',
    down: '#3485fa',
} as const;

class ThemeStore {
    savedTheme: ThemeType | null = null;
    systemColorScheme: 'light' | 'dark' | null = null;

    readonly lightColors = this.convertOklchColors(lightOklchColors);
    readonly darkColors = this.convertOklchColors(darkOklchColors);

    constructor() {
        makeObservable(this, {
            savedTheme: observable,
            systemColorScheme: observable,

            initialize: action,
            setTheme: action,

            themeToRender: computed,
            colors: computed,
        });
    }

    /**
     * 클라이언트 초기화 (HTML data-theme과 쿠키에서 테마 동기화)
     */
    initialize() {
        if (typeof document === 'undefined') {
            return;
        }

        // HTML의 data-theme 속성에서 현재 테마 읽기
        // - 서버에서 앱의 헤더나 쿠키를 통해서 렌더링했기 때문에 HTML 의 data-theme 속성은 이미 설정되어 있음
        // - 앱에 메시지를 보내서 앱에 저장된 테마를 가져올 수 있지만, 마크업의 것을 신뢰해도 됨
        const htmlTheme = document.documentElement.getAttribute('data-theme') as ThemeType;

        if (htmlTheme) {
            this.savedTheme = htmlTheme;
        } else {
            // 시스템 모드일 때
            // 초기값은 CSS 에서 읽어온다
            this.updateSystemColorScheme();

            // 모드 변경 감지
            window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
                if (e.matches) {
                    runInAction(() => {
                        this.systemColorScheme = 'dark';
                    });
                } else {
                    runInAction(() => {
                        this.systemColorScheme = 'light';
                    });
                }
            });
        }
    }

    private updateSystemColorScheme() {
        this.systemColorScheme = window.matchMedia('(prefers-color-scheme: dark)').matches
            ? 'dark'
            : 'light';
    }

    /**
     * 테마 변경
     */
    setTheme(
        theme: ThemeType,
        /**
         * 다른 웹뷰에 메세지를 전달함
         */
        shouldSendMessageToWebView: boolean = false,
    ) {
        if (this.savedTheme === theme) {
            return;
        }

        if (theme === 'light' || theme === 'dark') {
            this.savedTheme = theme;
        } else {
            // system 은 별도로 저장하지 않는다
            this.savedTheme = null;

            // 시스템으로 업데이트할 땐 컬러 스킴을 초기화해준다
            this.updateSystemColorScheme();
        }

        this.applyThemeToHtml(theme);

        if (webViewStore.isWebView) {
            if (shouldSendMessageToWebView) {
                webViewStore.sendMessageToApp('setAppTheme', {
                    theme: theme,
                });

                webViewStore.syncUi('themeChanged', {
                    theme: theme,
                });
            }
        } else {
            this.setThemeToCookie(theme);
        }
    }

    get themeToRender() {
        return this.savedTheme || this.systemColorScheme;
    }

    /**
     * 현재 테마의 색상 값 반환
     */
    get colors() {
        return this.themeToRender === 'dark' ? this.darkColors : this.lightColors;
    }

    /**
     * 쿠키에 테마 설정 저장
     */
    private setThemeToCookie(theme: ThemeType) {
        if (theme === 'light' || theme === 'dark') {
            // 1년 후 만료
            Cookies.set(THEME_COOKIE_NAME, theme, { expires: 365 });
        } else {
            // 시스템 테마일 경우 쿠키 삭제
            Cookies.remove(THEME_COOKIE_NAME);
        }
    }

    /**
     * HTML 태그에 data-theme 속성 설정
     */
    private applyThemeToHtml(theme: ThemeType) {
        if (typeof document === 'undefined') {
            return;
        }

        const html = document.documentElement;

        if (theme === 'light' || theme === 'dark') {
            html.setAttribute('data-theme', theme);
        } else {
            // 시스템 테마일 경우 data-theme 속성 제거 (daisyUI가 자동으로 시스템 테마를 따름)
            html.removeAttribute('data-theme');
        }
    }

    private oklchToHex(oklchString: string): string {
        try {
            const match = oklchString.match(/oklch\(([^)]+)\)/);
            if (!match) return '#000000';

            const values = match[1].split(/\s+/);
            const l = parseFloat(values[0].replace('%', '')) / 100;
            const c = parseFloat(values[1]);
            const h = parseFloat(values[2]) || 0;

            return formatHex(oklch({ mode: 'oklch', l, c, h })) || '#000000';
        } catch {
            return '#000000';
        }
    }

    private convertOklchColors<T extends Record<string, string>>(
        colors: T,
    ): Record<keyof T, string> {
        const result = {} as Record<keyof T, string>;
        for (const [key, value] of Object.entries(colors)) {
            result[key as keyof T] = this.oklchToHex(value);
        }
        return result;
    }

    opacity(hexColor: string, opacity: number): string {
        // opacity는 0-100 범위로 받아서 0-1로 변환
        const alpha = Math.max(0, Math.min(100, opacity)) / 100;

        // HEX를 RGB로 변환
        const hex = hexColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
}

export default new ThemeStore();
